'use client';

import {getPositions} from 'iq-positions/api';
import {getPositionIconComponent} from 'iq-positions/icons';
import type {Position} from 'iq-positions/types';
import type {OnboardingPosition} from 'iq-onboarding/types';
import {
  trackPositionAdded,
  trackPositionRemoved,
  trackPositionsSubmitted,
} from 'iq-onboarding/tracking';
import {useEffect, useMemo, useState} from 'react';

interface PositionsStepProps {
  // Suggested position ids, ordered by relevancy — pinned to the top of the list.
  suggestedIds: number[];
  value: OnboardingPosition[];
  onChange: (positions: OnboardingPosition[]) => void;
  onContinue: () => void;
}

export function PositionsStep({suggestedIds, value, onChange, onContinue}: PositionsStepProps) {
  const [positions, setPositions] = useState<Position[]>([]);

  useEffect(() => {
    getPositions()
      .then(setPositions)
      .catch(() => setPositions([]));
  }, []);

  // Suggested positions first (in relevancy order), then the rest alphabetically.
  const ordered = useMemo(() => {
    const rank = (id: number) => {
      const i = suggestedIds.indexOf(id);
      return i === -1 ? Infinity : i;
    };
    return [...positions].sort((a, b) => {
      const ra = rank(a.id);
      const rb = rank(b.id);
      if (ra !== rb) return ra - rb;
      return a.name.localeCompare(b.name);
    });
  }, [positions, suggestedIds]);

  function toggle(position: Position) {
    if (value.some((p) => p.id === position.id)) {
      onChange(value.filter((p) => p.id !== position.id));
      void trackPositionRemoved(position.slug, position.name);
    } else {
      onChange([...value, {id: position.id, slug: position.slug, name: position.name}]);
      void trackPositionAdded(position.slug, position.name, suggestedIds.includes(position.id));
    }
  }

  const canContinue = value.length >= 1;

  return (
    <div className='flex flex-col gap-6'>
      <div>
        <div className='flex flex-col justify-end lg:h-[4.5rem]'>
          <h1 className='text-2xl sm:text-3xl font-extrabold text-white'>
            Let&apos;s make your prep as relevant as possible
          </h1>
        </div>
        <p className='text-white/60 text-base mt-2'>Select the positions you&apos;re targeting.</p>
      </div>

      <div className='relative bg-white rounded-2xl p-6 flex flex-col gap-2'>
        <label className='text-sm font-semibold text-iq-black'>Target positions</label>
        <div className='flex flex-col gap-1 lg:max-h-[15rem] lg:overflow-y-auto lg:pr-1 lg:pb-10'>
          {ordered.map((position) => {
            const selected = value.some((p) => p.id === position.id);
            const Icon = getPositionIconComponent(position.name);
            return (
              <button
                key={position.id}
                type='button'
                onClick={() => toggle(position)}
                className={`flex items-center gap-2.5 w-full text-left rounded-lg border px-3 py-2 cursor-pointer transition-colors ${
                  selected
                    ? 'bg-iq-orange-light border-iq-orange text-iq-orange-dark'
                    : 'border-iq-grey-light text-iq-grey-dark hover:bg-iq-grey-lighter'
                }`}>
                <Icon
                  className={`w-4 h-4 shrink-0 transition-colors ${
                    selected ? 'text-iq-orange' : 'text-iq-grey-medium'
                  }`}
                />
                <span className='font-medium text-sm'>{position.name}</span>
              </button>
            );
          })}
        </div>
        <div className='pointer-events-none absolute inset-x-6 bottom-6 hidden lg:block h-10 bg-gradient-to-t from-white to-transparent' />
      </div>

      <button
        type='button'
        disabled={!canContinue}
        onClick={() => {
          void trackPositionsSubmitted(
            value.map((p) => p.slug),
            value.filter((p) => suggestedIds.includes(p.id)).length,
          );
          onContinue();
        }}
        className='w-full py-4 px-8 bg-iq-orange text-white text-base font-bold rounded-full hover:opacity-90 transition-opacity disabled:opacity-30'>
        Continue
      </button>
    </div>
  );
}
